import React from 'react';
import {Select, Form} from 'antd';
import {LabeledValue} from 'antd/es/select';

/**
 * A reusable select (dropdown) component with optional search functionality, using Ant Design's Select.
 *
 * @template T - The type of the options array elements.
 * @param {Object} props - The properties passed to the select component.
 * @param {T[]} props.options - An array of options for the dropdown menu. Each option should conform to the `LabeledValue` type or a compatible structure.
 * @param {boolean} props.showSearch - Determines whether the dropdown has a search bar to filter options.
 * @param {string} props.placeholder - Placeholder text displayed in the dropdown when no value is selected.
 * @param {string} props.label - Label text displayed above the dropdown.
 * @param {string} [props.value] - The currently selected value of the dropdown.
 * @param {boolean} [props.required] - Indicates if the field is required. Defaults to `false`.
 * @param {(value: string) => void} [props.onChange] - Optional callback triggered when the dropdown value changes.
 * @param {boolean} [props.disabled] - Disables the dropdown if `true`.
 * @param {string} [props.optionLabelProp] - Property of the option object to display as the label. Defaults to `'children'`.
 *
 * @returns {React.ReactElement} A styled select component with optional search and validation.
 */
type SelectProp<T> = {
    options: T[],
    showSearch: boolean;
    placeholder: string;
    label: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    value?: string;
    required?: boolean;
    optionLabelProp?: string;
}

const SelectComponent = <T, >({options, showSearch, placeholder, label, value, required, onChange, disabled}: SelectProp<T>) => {
    return (
        <div>
            <Form.Item
                validateStatus={required && !value ? 'error' : undefined}
                help={required && !value ? 'This field is required' : undefined}
            >
                <label style={{display: 'block'}}> {label}</label>
                <Select showSearch={showSearch} options={options as LabeledValue[]} placeholder={placeholder}
                    onChange={onChange} style={{width: '100%'}} optionFilterProp="children" value={value}
                    disabled={disabled}
                    defaultValue={options.length > 0 ? (options[0] as LabeledValue).value.toString() : undefined}
                />
            </Form.Item>
        </div>
    );
};

export default SelectComponent;